import { useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import RelativeTime from "dayjs/plugin/relativeTime";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Get, Put, Delete } from "../api/api";
import { printComanda } from "../api/printer";
import { GenerateComanda } from "../api/ticket";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useForm } from "react-hook-form";
import { Cart } from "../components/Cart";
import { CartContext } from "../context/cart";
import { UserContext } from "../context/user";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { Loading } from "../components/Loading";

const comandaFilters = [
  { key: "all", label: "Todas" },
  { key: "pending", label: "Pendientes" },
  { key: "finished", label: "Preparadas" },
];

// Cuántas líneas de producto se leen de un vistazo. El resto se resume en
// "+N productos" para que una comanda grande no empuje a las siguientes.
const VISIBLE_ITEMS = 3;

dayjs.extend(RelativeTime);
dayjs.locale("es");

function parseCart(value) {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatCurrency(value) {
  return `${Number(value || 0).toFixed(2)}€`;
}

function cartTotal(items) {
  return items.reduce(
    (sum, item) =>
      sum + Number(item.custom_price || item.price || 0) * Number(item.quantity || 0),
    0,
  );
}

export function Comandas() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const shopId = localStorage.getItem("shop_id");
  const exerciseId = localStorage.getItem("exercise_id");

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["comandas", shopId, exerciseId],
    queryFn: () => Get({ request: `/comandas?shop_id=${shopId}` }),
    // La cocina no debería tener que recargar a mano: la lista se mantiene
    // viva sola, igual que los avisos que disparan la impresión.
    refetchInterval: 15000,
  });

  const comandas = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // El carrito llega como texto JSON. Se parsea una vez por comanda y se
  // reparte a las tarjetas: el filtro por nombre de producto y el pintado de la
  // tarjeta comparten el mismo array.
  const itemsByComanda = useMemo(() => {
    const map = new Map();
    comandas.forEach((comanda) => map.set(comanda.id, parseCart(comanda.cart)));
    return map;
  }, [comandas]);

  const counts = useMemo(
    () => ({
      all: comandas.length,
      pending: comandas.filter((comanda) => !comanda.finished).length,
      finished: comandas.filter((comanda) => Boolean(comanda.finished)).length,
    }),
    [comandas],
  );

  const visibleComandas = useMemo(() => {
    const query = search.trim().toLowerCase();

    return comandas
      .filter((comanda) => {
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "pending" && !comanda.finished) ||
          (statusFilter === "finished" && Boolean(comanda.finished));
        if (!matchesStatus) return false;
        if (!query) return true;

        const haystack = [
          comanda.name,
          comanda.location,
          comanda.notes,
          String(comanda.id),
          // Buscar por producto localiza la comanda sin leerlas todas.
          ...(itemsByComanda.get(comanda.id) || []).map((item) => item.name),
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

        return haystack;
      })
      // Primero las pendientes y, dentro de cada grupo, la más antigua primero:
      // es el orden en el que se cocina.
      .sort((a, b) => {
        const aFinished = Boolean(a.finished);
        const bFinished = Boolean(b.finished);
        if (aFinished !== bFinished) return aFinished ? 1 : -1;
        const diff = dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf();
        return aFinished ? -diff : diff;
      });
  }, [comandas, itemsByComanda, search, statusFilter]);

  if (isLoading) return <Loading />;

  return (
    <div className="absolute inset-0 overflow-hidden bg-neutral-100 p-3 sm:p-4">
      <div className="flex h-full min-w-0 flex-col gap-4 lg:flex-row">
        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="shrink-0 border-b border-neutral-300 pb-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h1 className="flex items-center gap-2 text-2xl font-semibold text-neutral-900">
                  Comandas
                  <span className="rounded-full bg-neutral-200 px-2.5 py-0.5 text-sm font-semibold text-neutral-600">
                    {counts.all}
                  </span>
                </h1>
                <p className="text-sm text-neutral-500">
                  Gestiona los pedidos activos de cocina.
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => refetch()}
                disabled={isFetching}
                aria-label="Actualizar comandas"
                title="Actualizar"
                className="h-12 w-12 shrink-0 px-0 text-neutral-500"
              >
                <Icon
                  icon="mdi:refresh"
                  className={`text-xl ${isFetching ? "animate-spin" : ""}`}
                />
              </Button>
            </div>

            <div className="mt-3 flex gap-2">
              <div className="relative min-w-0 flex-1">
                <Icon
                  icon="mdi:magnify"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-neutral-400"
                />
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar por nombre, mesa, nota o producto…"
                  aria-label="Buscar comandas"
                  className="h-12 rounded-xl bg-white pl-10 pr-4 text-base shadow-none"
                />
              </div>
            </div>

            <div
              role="group"
              aria-label="Filtrar comandas por estado"
              className="mt-3 grid grid-cols-3 gap-1 rounded-xl bg-neutral-200/70 p-1"
            >
              {comandaFilters.map((filter) => {
                const active = statusFilter === filter.key;
                return (
                  <button
                    type="button"
                    key={filter.key}
                    onClick={() => setStatusFilter(filter.key)}
                    aria-pressed={active}
                    className={`flex h-11 min-w-0 touch-manipulation items-center justify-center gap-1.5 rounded-lg px-2 text-sm font-medium transition-colors active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
                      active
                        ? "bg-white text-blue-700 shadow-sm"
                        : "text-neutral-600 hover:bg-white/60"
                    }`}
                  >
                    <span className="truncate">{filter.label}</span>
                    <span
                      className={`rounded-full px-1.5 text-xs font-semibold tabular-nums ${
                        active ? "bg-blue-50 text-blue-700" : "bg-neutral-300/70 text-neutral-600"
                      }`}
                    >
                      {counts[filter.key]}
                    </span>
                  </button>
                );
              })}
            </div>
          </header>

          <div
            data-bottom-nav-scroll
            className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1"
            aria-busy={isFetching}
          >
            {visibleComandas.length === 0 ? (
              <div className="flex min-h-80 items-center justify-center">
                <EmptyState
                  description={
                    search || statusFilter !== "all"
                      ? "No hay comandas para este filtro"
                      : "No hay comandas"
                  }
                />
              </div>
            ) : (
              <ComandaList
                comandas={visibleComandas}
                itemsByComanda={itemsByComanda}
                refetch={refetch}
              />
            )}
          </div>
        </section>

        <aside className="h-[42%] min-h-0 shrink-0 lg:h-full lg:w-1/3">
          <Cart refetch={refetch} />
        </aside>
      </div>
    </div>
  );
}

function ComandaList({ comandas, itemsByComanda, refetch }) {
  const [animateRef] = useAutoAnimate();

  return (
    <div
      ref={animateRef}
      className="space-y-3 pb-[calc(1.25rem_+_var(--bottom-nav-reserve))]"
    >
      {comandas.map((comanda) => (
        <Comanda
          key={comanda.id}
          comanda={comanda}
          items={itemsByComanda.get(comanda.id) || []}
          refetch={refetch}
        />
      ))}
    </div>
  );
}

function Comanda({ comanda, items, refetch }) {
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [printing, setPrinting] = useState(false);
  const { setCart, setComanda } = useContext(CartContext);
  const { shop } = useContext(UserContext);
  const confirm = useConfirmDialog();
  const finished = Boolean(comanda.finished);
  const titleId = `comanda-${comanda.id}-title`;
  const total = cartTotal(items);

  const { register, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      name: comanda.name || "",
      location: comanda.location || "",
      notes: comanda.notes || "",
      time: comanda.time || 0,
      delivery: Boolean(comanda.delivery),
    },
  });

  async function save(values) {
    setLoading(true);
    try {
      await Put({
        request: `/comandas/${comanda.id}?shop_id=${localStorage.getItem("shop_id")}`,
        data: {
          ...values,
          delivery: values.delivery ? 1 : 0,
        },
      });
      await refetch();
      toast.success("Comanda actualizada correctamente");
      setEdit(false);
    } catch {
      toast.error("No se ha podido actualizar la comanda");
    } finally {
      setLoading(false);
    }
  }

  async function print() {
    if (printing) return;
    setPrinting(true);
    const toastId = toast.loading("Enviando la comanda a la impresora…");
    try {
      // El listado llega con carga selectiva, así que para imprimir hay que
      // pedir el detalle completo de la comanda, igual que hace el aviso de
      // comanda nueva: con el listado, el papel puede salir sin modificadores.
      const detail = await Get({
        request: `/comandas/${comanda.id}?shop_id=${localStorage.getItem("shop_id")}`,
      });
      const detailItems = parseCart(detail?.comanda?.cart);
      if (!detail?.success || detailItems.length === 0) {
        throw new Error("La comanda no tiene productos que imprimir");
      }

      const toPrint = GenerateComanda(
            { ...detail.comanda, cart: detailItems },
            shop?.ticket_settings?.paper_saving || false,
          );
          console.log('comanda manual: data a imprimir', {
            id: detail.comanda?.id,
            name: detail.comanda?.name,
            cartLen: detailItems.length,
            paperSaving: shop?.ticket_settings?.paper_saving || false,
          });
          const printed = await printComanda(toPrint);

          // printComanda ya avisa del fallo de impresora; el aviso de éxito solo
          // se muestra cuando el papel ha salido de verdad.
          if (printed) {
            toast.success("Comanda enviada a la impresora", { id: toastId });
          } else {
            toast.dismiss(toastId);
            console.warn('comanda manual: printComanda devolvió false');
          }
    } catch {
      toast.error("No se ha podido imprimir la comanda", { id: toastId });
    } finally {
      setPrinting(false);
    }
  }

  function loadComanda() {
    setCart(items);
    setComanda(comanda);
    toast.success(`Comanda ${comanda.id} cargada en el carrito`);
  }

  function finishComanda() {
    if (finished) return;
    confirm({
      title: "¿Marcar como preparada?",
      description: "La comanda dejará de aparecer como pendiente.",
      okText: "Marcar preparada",
      cancelText: "Cancelar",
      onOk: async () => {
        setFinishing(true);
        try {
          await Put({
            request: `/comandas/${comanda.id}/finish?shop_id=${localStorage.getItem("shop_id")}`,
          });
          await refetch();
          toast.success("Comanda marcada como preparada");
        } catch {
          toast.error("No se ha podido finalizar la comanda");
        } finally {
          setFinishing(false);
        }
      },
    });
  }

  function deleteComanda() {
    confirm({
      title: "¿Eliminar comanda?",
      description: "Esta acción no se puede deshacer.",
      okText: "Eliminar",
      cancelText: "Cancelar",
      variant: "danger",
      onOk: async () => {
        try {
          await Delete({
            request: `/comandas/${comanda.id}?shop_id=${localStorage.getItem("shop_id")}`,
          });
          await refetch();
          toast.success("Comanda eliminada");
        } catch {
          toast.error("No se ha podido eliminar la comanda");
        }
      },
    });
  }

  return (
    <>
      <Sheet open={edit} onOpenChange={setEdit}>
        <SheetContent className="w-full p-0 sm:max-w-[520px]">
          <SheetHeader className="border-b p-5">
            <SheetTitle>Editar comanda #{comanda.id}</SheetTitle>
            <Button
              type="button"
              loading={loading}
              onClick={handleSubmit(save)}
              className="mt-3 h-12 w-full text-base"
            >
              Guardar cambios
            </Button>
          </SheetHeader>
          <Spinner spinning={loading}>
            <form onSubmit={handleSubmit(save)} className="space-y-4 p-5">
              <div className="space-y-2">
                <Label htmlFor={`comanda-name-${comanda.id}`}>Nombre</Label>
                <Input id={`comanda-name-${comanda.id}`} {...register("name")} className="h-12 text-base" />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`comanda-location-${comanda.id}`}>Mesa</Label>
                <Input id={`comanda-location-${comanda.id}`} {...register("location")} className="h-12 text-base" />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`comanda-notes-${comanda.id}`}>Notas</Label>
                <Input id={`comanda-notes-${comanda.id}`} {...register("notes")} className="h-12 text-base" />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`comanda-time-${comanda.id}`}>Minutos</Label>
                <Input id={`comanda-time-${comanda.id}`} type="number" {...register("time")} className="h-12 text-base" />
              </div>
              <div className="flex min-h-14 items-center justify-between border-y border-neutral-200">
                <Label htmlFor={`comanda-delivery-${comanda.id}`}>Delivery</Label>
                <Switch
                  id={`comanda-delivery-${comanda.id}`}
                  checked={watch("delivery")}
                  onCheckedChange={(value) => setValue("delivery", value)}
                />
              </div>
            </form>
          </Spinner>
        </SheetContent>
      </Sheet>

      <article
        aria-labelledby={titleId}
        className={`relative rounded-2xl border border-l-4 p-3 shadow-sm transition-colors sm:p-4 ${
          finished
            ? "border-neutral-200 border-l-emerald-400 bg-neutral-100/70"
            : "border-neutral-200 border-l-amber-400 bg-white"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2
              id={titleId}
              className={`truncate text-lg font-semibold ${finished ? "text-neutral-600" : "text-neutral-900"}`}
            >
              <span className="font-normal text-neutral-400">#{comanda.id}</span>{" "}
              {comanda.name || "Sin nombre"}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500">
              <span className="inline-flex items-center gap-1">
                <Icon icon="mdi:table-chair" className="text-base" />
                {comanda.location || "Sin mesa"}
              </span>
              {Boolean(comanda.delivery) && (
                <span className="inline-flex items-center gap-1 font-medium text-violet-600">
                  <Icon icon="mdi:moped-electric" className="text-base" />
                  Delivery
                </span>
              )}
              {Number(comanda.time) > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Icon icon="mdi:timer-outline" className="text-base" />
                  {comanda.time} min
                </span>
              )}
            </div>
          </div>

          <span
            className={`inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-2.5 text-xs font-semibold ${
              finished ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
            }`}
          >
            <Icon icon={finished ? "mdi:check-circle" : "mdi:clock-outline"} className="text-sm" />
            {finished ? "Preparada" : "Pendiente"}
          </span>
        </div>

        {comanda.notes && (
          <p className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-2.5 py-2 text-sm text-amber-900">
            <Icon icon="mdi:message-text-outline" className="mt-0.5 shrink-0 text-base" />
            <span className="min-w-0">{comanda.notes}</span>
          </p>
        )}

        <ul className="mt-3 space-y-1.5">
          {items.length === 0 && (
            <li className="text-sm italic text-neutral-400">La comanda no tiene productos</li>
          )}
          {items.slice(0, VISIBLE_ITEMS).map((item, index) => (
            <li key={index} className="flex gap-2 text-sm leading-5">
              <span className="w-8 shrink-0 text-right font-semibold tabular-nums text-neutral-900">
                {item.quantity}×
              </span>
              <span className="min-w-0">
                <span className={finished ? "text-neutral-600" : "text-neutral-800"}>{item.name}</span>
                {(item.custom_mods || []).map((mod, modIndex) => (
                  <span key={modIndex} className="block text-xs text-neutral-500">
                    {mod.defaultEnabled ? `Sin ${mod.name}` : `Con ${mod.name}`}
                  </span>
                ))}
              </span>
            </li>
          ))}
          {items.length > VISIBLE_ITEMS && (
            <li className="pl-10 text-xs font-medium text-neutral-500">
              +{items.length - VISIBLE_ITEMS}{" "}
              {items.length - VISIBLE_ITEMS === 1 ? "producto más" : "productos más"}
            </li>
          )}
        </ul>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-neutral-500">
          <span className="inline-flex items-center gap-1">
            <Icon icon="mdi:clock-outline" className="text-base" />
            <time dateTime={dayjs(comanda.created_at).toISOString()}>
              {dayjs(comanda.created_at).fromNow()}
            </time>
          </span>
          <span>{items.length === 1 ? "1 artículo" : `${items.length} artículos`}</span>
          <span className="ml-auto text-base font-bold tabular-nums text-neutral-900">
            {formatCurrency(total)}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-neutral-200/70 pt-3">
          <Button
            type="button"
            variant="outline"
            onClick={loadComanda}
            aria-label={`Cargar la comanda ${comanda.id} en el carrito`}
            className="h-12 flex-1 px-4 text-base sm:flex-none"
          >
            <Icon icon="mdi:cart-plus" className="mr-2 text-xl" />
            Cargar
          </Button>

          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant={finished ? "outline" : "default"}
              loading={printing}
              onClick={print}
              aria-label={`Imprimir la comanda ${comanda.id}`}
              title={finished ? "Volver a imprimir" : "Imprimir"}
              className="h-12 px-4 text-base"
            >
              <Icon icon="mdi:printer-outline" className="mr-2 text-xl" />
              Imprimir
            </Button>

            <button
              type="button"
              onClick={finishComanda}
              disabled={finished || finishing}
              aria-pressed={finished}
              aria-label={
                finished
                  ? `La comanda ${comanda.id} ya está preparada`
                  : `Marcar la comanda ${comanda.id} como preparada`
              }
              title={finished ? "Ya está preparada" : "Marcar como preparada"}
              className={`flex h-12 w-12 shrink-0 touch-manipulation items-center justify-center rounded-xl border transition-colors active:scale-95 disabled:pointer-events-none ${
                finished
                  ? "border-emerald-200 bg-emerald-50 text-emerald-600"
                  : "border-neutral-200 bg-white text-neutral-500 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-600"
              }`}
            >
              <Icon icon={finished ? "mdi:check-bold" : "mdi:check"} className="text-xl" />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={`Más acciones de la comanda ${comanda.id}`}
                  title="Más acciones"
                  className="flex h-12 w-12 shrink-0 touch-manipulation items-center justify-center rounded-xl border border-neutral-200 bg-white text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 active:scale-95"
                >
                  <Icon icon="mdi:dots-horizontal" className="text-2xl" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[12rem] text-base">
                <DropdownMenuItem
                  onSelect={() => setEdit(true)}
                  className="h-12 gap-2 px-3"
                >
                  <Icon icon="mdi:pencil-outline" className="text-lg" />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={deleteComanda}
                  className="h-12 gap-2 px-3 text-red-500 focus:text-red-600"
                >
                  <Icon icon="mdi:trash-can-outline" className="text-lg" />
                  Eliminar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </article>
    </>
  );
}